import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  TextField,
  Stack,
  InputAdornment,
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Fade,
  Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useQuery } from "@apollo/client";
import AddIngredients from "../inventory/AddIngredients";
import { useNotification } from "../../Context/notificationContext";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { GET_INGREDIENTS } from "./ChooseGrainsForm";

export default function ChooseOilForm({ task, onSubmit }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedItem, setSelectedItem] = useState(task.item_name || "");
  const [quantity, setQuantity] = useState(task.quantity || "");
  const [error, setError] = useState("");
  const [itemError, setItemError] = useState("");
  const { showNotification } = useNotification();
  const minQuantity = 10;
  const maxQuantity = 500;

  const {
    data,
    loading: getIngredientsLoading,
    error: getIngredientsError,
    refetch: refetchIngredients,
  } = useQuery(GET_INGREDIENTS, {
    variables: {
      ingredientType: "OIL",
    },
    fetchPolicy: "cache-and-network",
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm()) {
      task.args = `${selectedItem} - ${quantity} ml`;
      task.quantity = quantity;
      task.item_name = selectedItem;
      onSubmit(task);
    }
  };

  const validateForm = () => {
    if (!selectedItem) {
      setItemError("Please select an Oil");
      setError("");
      return false;
    }
    if (+quantity < minQuantity || +quantity > maxQuantity) {
      setError(`Quantity must be between ${minQuantity} and ${maxQuantity} ml`);
      setItemError("");
      return false;
    }
    setError("");
    setItemError("");
    return true;
  };
  const [addIngredientActive, setAddIngredientActive] = useState(false);
  const [item_name, setItem_name] = useState("");
  const [ingredientType, setIngredientType] = useState(null);
  const [ingredientImage, setIngredientImage] = useState(null);
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    if (data && data.getIngredients) {
      setIngredients(data.getIngredients);
    }

    if (getIngredientsError) {
      showNotification("error", getIngredientsError.message);
    }
  }, [data, getIngredientsError, showNotification]);

  return addIngredientActive ? (
    // Add Ingredient
    <AddIngredients
      newIngredientName={item_name}
      setNewIngredientName={setItem_name}
      ingredientType={ingredientType}
      setIngredientType={setIngredientType}
      ingredientImage={ingredientImage}
      setIngredientImage={setIngredientImage}
      ingredients={ingredients}
      setIngredients={setIngredients}
      setAddIngredientActive={setAddIngredientActive}
      refetchIngredients={refetchIngredients}
    />
  ) : (
    <Fade in={true} timeout={500}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          background:
            theme.palette.mode === "dark"
              ? "rgba(66, 66, 66, 0.8)"
              : "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          transition: "all 0.3s ease",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                fontWeight="500"
                color="primary"
              >
                <LocalOfferIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Choose Oil
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Select an oil and specify the quantity needed
              </Typography>
              <Divider />
            </Box>

            <Autocomplete
              options={data?.getIngredients || []}
              getOptionLabel={(option) => option.ingredient_name || ""}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) =>
                  option.ingredient_name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Enter Oil Name"
                  variant="outlined"
                  value={selectedItem}
                  error={!!itemError}
                  helperText={itemError}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: getIngredientsLoading ? (
                      <CircularProgress
                        color="primary"
                        size={20}
                        sx={{ mr: 1 }}
                      />
                    ) : null,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.3s",
                      "&:hover, &.Mui-focused": {
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 0 0 2px rgba(144, 202, 249, 0.25)"
                            : "0 0 0 2px rgba(25, 118, 210, 0.25)",
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    padding: "10px 16px",
                    transition: "background 0.2s",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <img
                      src={option.image_url || "/placeholder-16x9.png"}
                      alt={option.ingredient_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      loading="lazy"
                    />
                  </Box>
                  <Typography variant="body1">
                    {option.ingredient_name}
                  </Typography>
                </Box>
              )}
              noOptionsText={
                getIngredientsLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                    }}
                  >
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    <Typography>Loading ingredients...</Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Ingredient not found in the list
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setAddIngredientActive(true)}
                      sx={{
                        borderRadius: 8,
                        px: 3,
                        py: 1,
                      }}
                    >
                      Add New Oil
                    </Button>
                  </Box>
                )
              }
              onInputChange={(event, newInputValue) =>
                setSelectedItem(newInputValue)
              }
              value={
                ingredients.find(
                  (item) => item.ingredient_name === selectedItem
                ) || null
              }
              fullWidth
              disabled={getIngredientsLoading || !data?.getIngredients?.length}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: itemError
                      ? theme.palette.error.main
                      : undefined,
                  },
                },
              }}
            />
            <FormControl fullWidth variant="outlined">
              <TextField
                label="Enter Quantity in ml"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                error={!!error}
                helperText={error}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ml</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s",
                    "&:hover, &.Mui-focused": {
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 0 0 2px rgba(144, 202, 249, 0.25)"
                          : "0 0 0 2px rgba(25, 118, 210, 0.25)",
                    },
                  },
                }}
              />
            </FormControl>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size={isMobile ? "large" : "medium"}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: theme.shadows[4],
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: theme.shadows[8],
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Fade>
  );
}
